import React, { useState } from 'react';
import { useThemeContext } from '../../contexts/ThemeContext';
import { TransactionEntity } from '../../models/TransactionEntity';
import { TransactionEntityFetchFilter } from '../../models/TransactionEntityFetchFilter';
import { TransactionEntitiesPagination } from '../../api/transactions';
import { Button } from '../ui/Button';
import { Select } from '../ui/Select';
import searchIcon from '../../assets/images/search.png';
import filterIcon from '../../assets/images/filter.png';
import filterIconDark from '../../assets/images-night/filter.png';
import editIcon from '../../assets/images/edit.png';
import editIconDark from '../../assets/images-night/edit.png';
import deleteIcon from '../../assets/images/delete.png';
import deleteIconDark from '../../assets/images-night/delete.png';
import plusIcon from '../../assets/images/plus.png';

interface TransactionEntityListProps {
  transactionEntities: TransactionEntity[];
  loading: boolean;
  filter: TransactionEntityFetchFilter;
  onFilterChange: (key: keyof TransactionEntityFetchFilter, value: any) => void;
  onEdit: (entity: TransactionEntity) => void;
  onDelete: (entity: TransactionEntity) => void;
  onNewEntity: () => void;
  onEntityClick?: (entity: TransactionEntity) => void;
  pagination?: TransactionEntitiesPagination | null;
}

const TransactionEntityList: React.FC<TransactionEntityListProps> = ({
  transactionEntities,
  loading,
  filter,
  onFilterChange,
  onEdit,
  onDelete,
  onNewEntity,
  onEntityClick,
  pagination,
}) => {
  const [showFilters, setShowFilters] = useState(false);
  const { isDark } = useThemeContext();

  const currentPage = pagination?.page ?? filter.page ?? 1;
  const lastPage = pagination?.lastPage ?? 1;
  const total = pagination?.total ?? transactionEntities.length;
  const from = pagination?.from ?? (transactionEntities.length > 0 ? 1 : 0);
  const to = pagination?.to ?? transactionEntities.length;
  const hasNextPage = currentPage < lastPage;
  const hasPrevPage = currentPage > 1;

  // Get icon based on theme
  const getIcon = (lightIcon: string, darkIcon: string) => {
    return isDark ? darkIcon : lightIcon;
  };

  const categoryOptions = [
    { value: '', label: 'All Categories' },
    { value: 'action', label: 'Action' },
    { value: 'object', label: 'Object' },
    { value: 'indirect_object', label: 'Indirect Object' },
    { value: 'media', label: 'Payment Media' },
  ];

  const typeOptions = [
    { value: '', label: 'All Types' },
    { value: 'credit', label: 'Credit' },
    { value: 'debit', label: 'Debit' },
    { value: 'tradable', label: 'Tradable' },
    { value: 'non_tradable', label: 'Non-tradable' },
    { value: 'payable', label: 'Payable' },
    { value: 'third_party', label: 'Third party' },
  ];

  const handleRowClick = (entity: TransactionEntity, e: React.MouseEvent) => {
    // Don't trigger row click if edit/delete button was clicked
    if ((e.target as HTMLElement).closest('button')) {
      return;
    }
    if (onEntityClick) {
      onEntityClick(entity);
    }
  };

  return (
    <div className="bg-light rounded-lg p-6">
      {/* Header and actions */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div>
          <h4 className="text-lg">Transaction Entities ({pagination ? total : transactionEntities.length})</h4>
          <p className="text-sm text-grey_dark mt-1">Manage actions, objects, and payment methods</p>
        </div>
        <Button 
          onClick={onNewEntity}
          variant="filled-primary" 
          className="flex items-center w-full sm:w-auto justify-center"
        >
          <img
            src={plusIcon}
            alt="Add"
            className="object-contain w-4 h-4 mr-2"
          />
          New Entity
        </Button>
      </div>

      <hr className="flex-grow border-t border-grey_light mb-4" />

      {/* Search and filter section */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center w-full gap-4">
          <div className="relative w-full sm:w-64">
              <img
                src={searchIcon}
                alt="Search"
                className="absolute left-4 top-1/2 transform -translate-y-1/2 w-4 h-4"
              />
            <input
              type="text"
              placeholder="Search entities..."
              className="pl-10 pr-0 py-2 bg-light text-dark placeholder-grey_dark w-full border border-grey_light rounded-lg focus:outline-none focus:border-secondary"
              value={filter.name || ''}
              onChange={(e) => onFilterChange('name', e.target.value)}
            />
          </div>
          <Button 
            variant="google" 
            className="flex items-center w-full sm:w-auto justify-center"
            onClick={() => setShowFilters(!showFilters)}
          >
            <img
              src={getIcon(filterIcon, filterIconDark)}
              alt="Filter"
              className="object-contain w-4 h-4 mr-2"
            />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </Button>
        </div>

        {/* Advanced filters */}
        {showFilters && (
          <div className="mt-4 p-4 bg-bg_grey_light rounded-lg grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Category</label>
              <Select
                options={categoryOptions}
                value={filter.category || ''}
                onChange={(value) => onFilterChange('category', value || undefined)}
                placeholder="Select category"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Type</label>
              <Select
                options={typeOptions}
                value={filter.type || ''}
                onChange={(value) => onFilterChange('type', value || undefined)}
                placeholder="Select type"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Linked to KPI</label>
              <Select
                options={[
                  { value: 'true', label: 'Yes' },
                  { value: 'false', label: 'No' },
                  { value: '', label: 'All' },
                ]}
                value={filter.linked_to_kpi === undefined ? '' : filter.linked_to_kpi.toString()}
                onChange={(value) => onFilterChange('linked_to_kpi', value === '' ? undefined : value === 'true')}
              />
            </div>
          </div>
        )}
      </div>

      {/* Transaction entities list */}
      {loading ? (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <p className="mt-2 text-grey_dark">Loading transaction entities...</p>
        </div>
      ) : transactionEntities.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-grey_dark">No transaction entities found</p>
          <p className="text-sm text-grey_dark mt-1">Click the "New Entity" button to create your first transaction entity</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-grey_light">
                <th className="text-left py-3 px-2 text-grey_dark font-normal">Name</th>
                <th className="text-left py-3 px-2 text-grey_dark font-normal">Category</th>
                <th className="text-left py-3 px-2 text-grey_dark font-normal">Type</th>
                <th className="text-left py-3 px-2 text-grey_dark font-normal">Description</th>
                <th className="text-left py-3 px-2 text-grey_dark font-normal">KPI Linked</th>
                <th className="text-left py-3 px-2 text-grey_dark font-normal">Actions</th>
              </tr>
            </thead>
            <tbody>
              {transactionEntities.map((entity) => (
                <tr 
                  key={entity.id} 
                  className="border-b border-grey_light hover:bg-bg_grey_light cursor-pointer transition-colors duration-150"
                  onClick={(e) => handleRowClick(entity, e)}
                >
                  <td className="py-3 px-2">
                    <div className="font-medium">{entity.transaction_entity_name || 'Unnamed'}</div>
                  </td>
                  <td className="py-3 px-2">
                    <div className="text-sm">
                      <span className="px-2 py-1 bg-bg_grey_light rounded text-grey_dark">
                        {entity.transaction_entity_category || 'Uncategorized'}
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-2">
                    <div className="text-sm">{entity.transaction_entity_type || 'N/A'}</div>
                  </td>
                  <td className="py-3 px-2">
                    <div className="text-sm text-grey_dark truncate max-w-xs">
                      {entity.transaction_entity_description || 'No description'}
                    </div>
                  </td>
                  <td className="py-3 px-2">
                    <div className="text-sm">
                      {entity.linked_to_kpi ? (
                        <span className="px-2 py-1 bg-secondary_light text-primary rounded">Yes</span>
                      ) : (
                        <span className="px-2 py-1 bg-grey_light text-grey_dark rounded">No</span>
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-2">
                    <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => onEdit(entity)}
                        className="p-1 hover:bg-bg_grey_light rounded transition-colors duration-150"
                        title="Edit"
                      >
                        <img src={getIcon(editIcon, editIconDark)} alt="Edit" className="w-8 h-8" />
                      </button>
                      <button
                        onClick={() => onDelete(entity)}
                        className="p-1 hover:bg-bg_grey_light rounded text-danger transition-colors duration-150"
                        title="Delete"
                      >
                        <img src={getIcon(deleteIcon, deleteIconDark)} alt="Delete" className="w-8 h-8" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {(transactionEntities.length > 0 || total > 0) && (
        <div className="flex justify-between items-center mt-6 pt-4">
          <div className="text-sm text-grey_dark">
            {total === 0
              ? 'No entities'
              : `Showing ${from} to ${to} of ${total} entities`}
          </div>
          <div className="flex gap-2">
            <Button
              variant="google"
              onClick={() => onFilterChange('page', Math.max(1, currentPage - 1))}
              disabled={!hasPrevPage}
              className="text-grey_dark"
            >
              Previous
            </Button>
            <Button
              variant="google"
              onClick={() => onFilterChange('page', currentPage + 1)}
              disabled={!hasNextPage}
              className="text-grey_dark"
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionEntityList;


